import React from 'react';
import uniq from 'lodash/uniq';

interface State<T> {
  error: string | null;
  items: T[];
  activeItemIndex: number | null;
  inputValue: string;
}

enum ActionEnum {
  SET_ITEMS,
  ADD_ITEMS,
  UPDATE_ITEM,
  CLEAR_ITEMS,
  DELETE_ITEM,
  SET_INPUT_VALUE,
  START_EDIT,
  CANCEL_EDIT,
}

interface SetItems<T> {
  type: ActionEnum.SET_ITEMS;
  newItems: State<T>['items'];
}

interface AddItems<T> {
  type: ActionEnum.ADD_ITEMS;
  newItems: State<T>['items'];
}

interface UpdateItem<T> {
  type: ActionEnum.UPDATE_ITEM;
  prevValue: T;
  newValue: T;
}

interface DeleteItem<T> {
  type: ActionEnum.DELETE_ITEM;
  value: T;
}
interface ClearItems {
  type: ActionEnum.CLEAR_ITEMS;
}

interface SetInputValue {
  type: ActionEnum.SET_INPUT_VALUE;
  value: string;
}

interface StartEdit<T> {
  type: ActionEnum.START_EDIT;
  value: T;
}
interface CancelEdit {
  type: ActionEnum.CANCEL_EDIT;
}
/* The available actions that can be dispatched */
type Actions<T> =
  | SetItems<T>
  | AddItems<T>
  | UpdateItem<T>
  | ClearItems
  | DeleteItem<T>
  | SetInputValue
  | StartEdit<T>
  | CancelEdit;

export function checkDuplicates(newValue: string, existingItems: any[]): string | null {
  const includesMultipleValues = newValue.includes(',');
  if (includesMultipleValues) {
    // TODO: check multiple value for duplicates
    // const values = newValue.split(',');
    return null;
  }
  const valueExists = existingItems.find(e => e === newValue);
  if (valueExists) {
    return 'Value exists already';
  }
  return null;
}

export type Reducer<T> = React.Reducer<State<T>, Actions<T>>;

const multiValueReducer =
  <T,>(
    validateAddition?: (userEnteredInput: T, existing: T[], splitOnComma: boolean) => boolean,
    splitOnComma = false,
  ) =>
  (state: State<T>, action: Actions<T>): State<T> => {
    switch (action.type) {
      case ActionEnum.ADD_ITEMS: {
        // Checking for duplicate values or empty entry
        const newValidItems = action.newItems
          .filter(item => item !== '' && validateAddition(item, state.items, splitOnComma))
          .reverse();

        const validItems = uniq([...newValidItems, ...state.items]);
        const invalidItems = action.newItems.filter(
          item => !validateAddition(item, state.items, splitOnComma),
        );
        return {
          ...state,
          items: validItems,
          error: invalidItems.length
            ? `One or more invalid values were ignored: ${invalidItems.join(',')}`
            : null,
          activeItemIndex: null,
          inputValue: '',
        };
      }
      case ActionEnum.UPDATE_ITEM: {
        const indexToUpdate = state.items.indexOf(action.prevValue);
        const items = [...state.items];
        items[indexToUpdate] = action.newValue;
        return { ...state, items, activeItemIndex: null };
      }
      case ActionEnum.CLEAR_ITEMS:
        return { ...state, items: [], activeItemIndex: null };
      case ActionEnum.DELETE_ITEM: {
        const indexToDelete = state.items.indexOf(action.value);
        const items = [...state.items];
        items.splice(indexToDelete, 1);
        return { ...state, items, activeItemIndex: null };
      }
      case ActionEnum.SET_INPUT_VALUE: {
        const inputValue = action.value;
        const error =
          checkDuplicates(inputValue, state.items) ||
          (validateAddition(inputValue as T, state.items, splitOnComma) ? null : 'Invalid value');
        return { ...state, inputValue: action.value, error };
      }
      case ActionEnum.START_EDIT: {
        const indexToStartEdit = state.items.indexOf(action.value);
        return { ...state, activeItemIndex: indexToStartEdit };
      }
      case ActionEnum.CANCEL_EDIT:
        return { ...state, activeItemIndex: null };
      default:
        return state;
    }
  };

type MultiValueReducerProps<T> = {
  initialItems: T[];
  validateAddition?: (userEnteredInput: T, existing: T[], splitOnComma: boolean) => boolean;
  splitOnComma?: boolean;
};
function useMultiValueReducer<T>(props: MultiValueReducerProps<T>) {
  const { initialItems, validateAddition = () => true } = props;
  const initialState: State<T> = {
    error: null,
    items: initialItems,
    activeItemIndex: null,
    inputValue: '',
  };
  const [state, dispatch] = React.useReducer<Reducer<T>>(
    multiValueReducer(validateAddition, props.splitOnComma),
    initialState,
  );

  const addItems = React.useCallback(
    (newItems: T[]) =>
      dispatch({
        type: ActionEnum.ADD_ITEMS,
        newItems,
      }),
    [dispatch],
  );

  const updateItem = React.useCallback(
    (prevValue: T, newValue: T) =>
      dispatch({
        type: ActionEnum.UPDATE_ITEM,
        prevValue,
        newValue,
      }),
    [dispatch],
  );

  const clearItems = React.useCallback(
    () =>
      dispatch({
        type: ActionEnum.CLEAR_ITEMS,
      }),
    [dispatch],
  );

  const deleteItem = React.useCallback(
    (value: T) =>
      dispatch({
        type: ActionEnum.DELETE_ITEM,
        value,
      }),
    [dispatch],
  );

  const setInputValue = React.useCallback(
    value =>
      dispatch({
        type: ActionEnum.SET_INPUT_VALUE,
        value,
      }),
    [dispatch],
  );

  const editItem = React.useCallback(
    value => dispatch({ type: ActionEnum.START_EDIT, value }),
    [dispatch],
  );

  const cancelEdit = React.useCallback(
    () => dispatch({ type: ActionEnum.CANCEL_EDIT }),
    [dispatch],
  );

  return {
    ...state,
    addItems,
    updateItem,
    clearItems,
    deleteItem,
    setInputValue,
    editItem,
    cancelEdit,
  };
}

export default useMultiValueReducer;
